import { z } from "zod";
import { discordSnowflakeSchema, utcTimestampSchema, } from "./guild-settings.js";
import { boundedNfcTextSchema, sanitizedJsonObjectSchema, uuidSchema, } from "./platform-common.js";
export const leadershipLinkSourceSchema = z.enum([
    "owner_claim",
    "transfer",
    "superadmin_recovery",
]);
export const leadershipTransferStatusSchema = z.enum([
    "pending",
    "accepted",
    "declined",
    "cancelled",
    "expired",
    "superseded",
]);
export const guildLeaderLinkSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    leaderUserId: discordSnowflakeSchema,
    revision: z.int().positive(),
    linkedAt: utcTimestampSchema,
    linkedByUserId: discordSnowflakeSchema.nullable(),
    linkSource: leadershipLinkSourceSchema,
    updatedAt: utcTimestampSchema,
})
    .strict()
    .refine((value) => Date.parse(value.updatedAt) >= Date.parse(value.linkedAt), {
    message: "Leadership update must not precede the link",
    path: ["updatedAt"],
});
export const guildLeaderTransferSchema = z
    .object({
    transferId: uuidSchema,
    guildId: discordSnowflakeSchema,
    fromUserId: discordSnowflakeSchema,
    toUserId: discordSnowflakeSchema,
    status: leadershipTransferStatusSchema,
    expectedLinkRevision: z.int().positive(),
    initiatedAt: utcTimestampSchema,
    expiresAt: utcTimestampSchema,
    finalizedAt: utcTimestampSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if (value.fromUserId === value.toUserId) {
        context.addIssue({
            code: "custom",
            message: "Transfer recipient must differ from the current leader",
            path: ["toUserId"],
        });
    }
    if ((value.status === "pending") !== (value.finalizedAt === null)) {
        context.addIssue({
            code: "custom",
            message: "Only pending transfers omit the finalized timestamp",
            path: ["finalizedAt"],
        });
    }
    if (Date.parse(value.expiresAt) <= Date.parse(value.initiatedAt)) {
        context.addIssue({
            code: "custom",
            message: "Transfer expiry must follow initiation",
            path: ["expiresAt"],
        });
    }
    if (value.finalizedAt !== null &&
        Date.parse(value.finalizedAt) < Date.parse(value.initiatedAt)) {
        context.addIssue({
            code: "custom",
            message: "Transfer finalization must not precede initiation",
            path: ["finalizedAt"],
        });
    }
});
export const guildOwnershipSchema = z
    .object({
    guildId: discordSnowflakeSchema,
    synchronizedOwnerUserId: discordSnowflakeSchema.nullable(),
    leader: guildLeaderLinkSchema.nullable(),
    pendingTransfer: guildLeaderTransferSchema.nullable(),
})
    .strict()
    .superRefine((value, context) => {
    if ((value.leader !== null && value.leader.guildId !== value.guildId) ||
        (value.pendingTransfer !== null &&
            value.pendingTransfer.guildId !== value.guildId)) {
        context.addIssue({
            code: "custom",
            message: "Ownership records must belong to the requested guild",
        });
    }
    if (value.pendingTransfer !== null &&
        (value.leader === null ||
            value.pendingTransfer.status !== "pending" ||
            value.pendingTransfer.fromUserId !== value.leader.leaderUserId ||
            value.pendingTransfer.expectedLinkRevision !== value.leader.revision)) {
        context.addIssue({
            code: "custom",
            message: "Pending transfer must match the current linked leader",
            path: ["pendingTransfer"],
        });
    }
});
export const guildOwnershipClaimSchema = z.object({}).strict();
export const guildLeaderTransferCreateSchema = z
    .object({
    toUserId: discordSnowflakeSchema,
    expectedLinkRevision: z.int().positive(),
})
    .strict();
export const guildLeaderTransferAcceptanceSchema = z
    .object({
    expectedCurrentLeaderUserId: discordSnowflakeSchema,
    expectedLinkRevision: z.int().positive(),
})
    .strict();
export const guildLeaderRecoverySchema = z
    .object({
    leaderUserId: discordSnowflakeSchema,
    expectedLinkRevision: z.int().positive().nullable(),
    reason: boundedNfcTextSchema(500, 2_048),
})
    .strict();
export const guildLeadershipConflictSchema = z
    .object({
    code: z.literal("guild_leadership_conflict"),
    message: z.string().trim().min(1).max(500),
    requestId: z.string().trim().min(1).max(128),
    current: guildOwnershipSchema,
})
    .strict();
export const leadershipAuditDisplaySchema = z
    .object({
    eventId: uuidSchema,
    guildId: discordSnowflakeSchema,
    transferId: uuidSchema.nullable(),
    actorUserId: discordSnowflakeSchema.nullable(),
    action: z.enum([
        "claim",
        "transfer_initiated",
        "transfer_accepted",
        "transfer_declined",
        "transfer_cancelled",
        "transfer_expired",
        "transfer_superseded",
        "superadmin_recovery",
    ]),
    reason: boundedNfcTextSchema(500, 2_048).nullable(),
    metadata: sanitizedJsonObjectSchema(8_192).nullable(),
    createdAt: utcTimestampSchema,
})
    .strict();
//# sourceMappingURL=guild-leadership.js.map